import { useCallback, useEffect, useMemo, useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import { Button, DialogTextContent, Divider, Flex, Icon, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { ProfileTypes } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/ProfileBaseballCard.utils";
import { ProfileHeadingConfig } from "./ProfileHeadingConfig";
import { ProfileFieldsConfig } from "./ProfileFieldsConfig";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import cx from "classnames";
import { LookupRecord } from "@/Utils/Promises";
import { filterInternalFields } from "@/Utils/FilterInternalFields";
import { ProfilePreviewModal } from "./ProfilePreviewModal";
import {
  restrictedInternalFields,
  kPositionCountFields,
  kEmployeeCountFields
} from "@coreapp/constants";
import {
  getHeadingSwapContent,
  PROFILE_TEST_PREFIX,
  RequestHeadingFieldSwapFn
} from "./ProfileConfiguration.utils";
import { AgentProfileConfig } from "./AgentProfileConfig";
import {
  buildAgentSampleRecord,
  getAgentSettingsFromHeadingConfig,
  isAgentProfileEnabled
} from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentProfile.constants";

type ProfileConfigurationProps = {
  chartConfig: any;
  handleUpdate: (config: any, fieldChanged: string) => void;
};

type CollapsibleCardTitleProps = {
  title: string;
  isOpen: boolean;
  textSize: "medium" | "large";
  iconSize?: "medium";
};

// The toggle lives inside the heading so the title stays exposed as a heading
const CollapsibleCardTitle = ({ title, isOpen, textSize, iconSize }: CollapsibleCardTitleProps) => (
  <Text modifier="bold" size={textSize} element="h2">
    <button type="button" className="oc-profile-configuration-card-toggle" aria-expanded={isOpen}>
      <span>{title}</span>
      <Icon name={isOpen ? "chevron-up" : "chevron-down"} size={iconSize} />
    </button>
  </Text>
);

const commonCardTitleContainerProps = {
  element: "h3",
  size: "small",
  type: "body",
  modifier: null,
  style: null
};

export const ProfileConfiguration = ({ chartConfig, handleUpdate }: ProfileConfigurationProps) => {
  let wsMgr = StateLib.GetWSMgr();
  const eventTracker = wsMgr?.GetUsageTracker();

  useEffect(() => {
    eventTracker?.ReportEvent("chart_settings.profile.opened");
  }, []);
  const [modalElement, setModalElement] = useState<any>(null);
  const [cardStatus, setCardStatus] = useState({
    employeeCardOpen: true,
    positionCardOpen: true,
    agentCardOpen: true
  });
  const isPositionChart = IsPositionChart(chartConfig);
  const showAgentCard = isAgentProfileEnabled();
  const isEmployeeCardCollapsible = isPositionChart || showAgentCard;

  const setUpModalElement = (type: ProfileTypes) => {
    if (type === ProfileTypes.Agent) {
      // No record lookup — LookupRecord resolves by box ID, which is never an
      // agent record; preview from a canned sample keyed by current mappings.
      setModalElement({
        record: buildAgentSampleRecord(
          getAgentSettingsFromHeadingConfig(chartConfig?.profile?.headingConfig).fields
        ),
        type: ProfileTypes.Agent
      });
      return;
    }

    const isPositionElement = type == ProfileTypes.Position;
    const ids = isPositionElement == isPositionChart ? [chartConfig.topBoxID] : [];

    LookupRecord(ids, chartConfig.ID, false, isPositionElement, {
      getFirstHolder: !isPositionElement && isPositionChart
    }).then(response => {
      const responseObj = JSON.parse(response);
      setModalElement({
        record: isPositionElement == isPositionChart ? responseObj.data?.[0] : responseObj.data,
        type: isPositionElement ? ProfileTypes.Position : ProfileTypes.Employee
      });
    });
  };

  const [employeeFields, positionFields, agentFields] = useMemo(() => {
    const employeeFields: string[] = [];
    const positionFields: string[] = [];
    const agentFields: string[] = [];

    var headersList = [];
    if (chartConfig["headers"] != null) {
      headersList = chartConfig["headers"];
    }

    var dynFields = [];
    dynFields = chartConfig["scriptFormulas"]; // dynamic fields
    var availableFields = headersList;

    if (dynFields != null) {
      dynFields = dynFields.map((x: any) => x.formulaName);
      availableFields = availableFields.concat(dynFields);
    }

    // System-generated fields to include based on chart type
    const internalFields = isPositionChart
      ? [...kPositionCountFields, "Level", "SubChart Level"]
      : [...kEmployeeCountFields, "Level", "SubChart Level"];

    availableFields = availableFields.concat(
      internalFields.filter(function (aField) {
        return availableFields.indexOf(aField) === -1;
      })
    );

    availableFields = filterInternalFields(availableFields);
    availableFields = availableFields.filter((f: string) => !restrictedInternalFields.includes(f));

    // Agent slots may map columns from either side of the holder/position
    // split, so they pick from the full filtered pool (INS-7).
    agentFields.push(...availableFields);

    // Filter out employee count fields from position charts and vice versa
    if (isPositionChart) {
      availableFields = availableFields.filter(
        (f: string) => !kEmployeeCountFields.includes(f as any)
      );

      const holderFields = new Set<string>(chartConfig.holderFields || []);
      employeeFields.push(...availableFields.filter((f: string) => holderFields.has(f)));
      positionFields.push(...availableFields.filter((f: string) => !holderFields.has(f)));
    } else {
      availableFields = availableFields.filter(
        (f: string) => !kPositionCountFields.includes(f as any)
      );
      employeeFields.push(...availableFields);
    }

    return [employeeFields, positionFields, agentFields];
  }, [chartConfig]);

  const requestHeadingFieldSwap = useCallback<RequestHeadingFieldSwapFn>(
    (fieldName, onConfirm, action, scope) => {
      const { title, message, question, confirmLabel } = getHeadingSwapContent(
        fieldName,
        action,
        scope
      );
      if (!wsMgr?.CreateReactAlert) {
        onConfirm();
        return;
      }
      wsMgr.CreateReactAlert(
        title,
        <Flex direction="column" gap="16px">
          <DialogTextContent>{message}</DialogTextContent>
          <DialogTextContent>{question}</DialogTextContent>
        </Flex>,
        onConfirm,
        () => {},
        confirmLabel,
        appstr("Cancel")
      );
    },
    [wsMgr]
  );

  return (
    <SectionCardGroup customClass="oc-profile-configuration">
      <Flex direction="column" justifyContent="flex-start">
        <Flex
          gap="large"
          justifyContent="space-between"
          alignItems="center"
          className={cx(
            "oc-profile-configuration-card-header",
            isEmployeeCardCollapsible ? "oc-profile-configuration-card-header-collapsible" : ""
          )}
          divProps={
            isEmployeeCardCollapsible
              ? {
                  onClick: () =>
                    setCardStatus(prev => ({ ...prev, employeeCardOpen: !prev.employeeCardOpen }))
                }
              : undefined
          }
          dataTestId={
            !isEmployeeCardCollapsible
              ? `${PROFILE_TEST_PREFIX}-employee-card`
              : cardStatus.employeeCardOpen
                ? `${PROFILE_TEST_PREFIX}-employee-card-collapse`
                : `${PROFILE_TEST_PREFIX}-employee-card-expand`
          }
        >
          {isEmployeeCardCollapsible ? (
            <CollapsibleCardTitle
              title={appstr("Employee profile card")}
              isOpen={cardStatus.employeeCardOpen}
              textSize="medium"
            />
          ) : (
            <Text modifier="bold" size="medium" element="h2">
              {appstr("Employee profile card")}
            </Text>
          )}
        </Flex>
        <SectionCard
          title={appstr("Employee profile desc")}
          titleContent={
            <Button
              modifier="secondary"
              onClick={() => setUpModalElement(ProfileTypes.Employee)}
              dataTestId={`${PROFILE_TEST_PREFIX}-employee-preview-button`}
            >
              {appstr("Preview")}
            </Button>
          }
          customSectionCardClass={cx(
            cardStatus.employeeCardOpen || !isEmployeeCardCollapsible
              ? ""
              : "oc-profile-configuration-card-collapsed"
          )}
          titleContainerProps={commonCardTitleContainerProps}
        >
          <Flex gap="16px" direction="column">
            <ProfileHeadingConfig
              type={ProfileTypes.Employee}
              config={chartConfig}
              fields={employeeFields}
              handleUpdate={handleUpdate}
              isPositionChart={isPositionChart}
              requestHeadingFieldSwap={requestHeadingFieldSwap}
            />
            <Divider />
            <ProfileFieldsConfig
              type={ProfileTypes.Employee}
              config={chartConfig}
              fields={employeeFields}
              handleUpdate={handleUpdate}
            />
          </Flex>
        </SectionCard>
      </Flex>
      {isPositionChart && (
        <Flex direction="column" justifyContent="flex-start">
          <Flex
            gap="large"
            justifyContent="space-between"
            alignItems="center"
            className="oc-profile-configuration-card-header oc-profile-configuration-card-header-collapsible"
            divProps={{
              onClick: () =>
                setCardStatus(prev => ({ ...prev, positionCardOpen: !prev.positionCardOpen }))
            }}
            dataTestId={
              cardStatus.positionCardOpen
                ? `${PROFILE_TEST_PREFIX}-position-card-collapse`
                : `${PROFILE_TEST_PREFIX}-position-card-expand`
            }
          >
            <CollapsibleCardTitle
              title={appstr("Position profile card")}
              isOpen={cardStatus.positionCardOpen}
              textSize="large"
              iconSize="medium"
            />
          </Flex>
          <SectionCard
            title={appstr("Position profile desc")}
            titleContent={
              <Button
                modifier="secondary"
                onClick={() => setUpModalElement(ProfileTypes.Position)}
                dataTestId={`${PROFILE_TEST_PREFIX}-position-preview-button`}
              >
                {appstr("Preview")}
              </Button>
            }
            customSectionCardClass={cx(
              cardStatus.positionCardOpen ? "" : "oc-profile-configuration-card-collapsed"
            )}
            titleContainerProps={commonCardTitleContainerProps}
          >
            <Flex gap="16px" direction="column">
              <ProfileHeadingConfig
                type={ProfileTypes.Position}
                config={chartConfig}
                fields={positionFields}
                handleUpdate={handleUpdate}
                isPositionChart
                requestHeadingFieldSwap={requestHeadingFieldSwap}
              />
              <Divider />
              <ProfileFieldsConfig
                type={ProfileTypes.Position}
                config={chartConfig}
                fields={positionFields}
                handleUpdate={handleUpdate}
              />
            </Flex>
          </SectionCard>
        </Flex>
      )}
      {showAgentCard && (
        <Flex direction="column" justifyContent="flex-start">
          <Flex
            gap="large"
            justifyContent="space-between"
            alignItems="center"
            className="oc-profile-configuration-card-header oc-profile-configuration-card-header-collapsible"
            divProps={{
              onClick: () =>
                setCardStatus(prev => ({ ...prev, agentCardOpen: !prev.agentCardOpen }))
            }}
            dataTestId={
              cardStatus.agentCardOpen
                ? `${PROFILE_TEST_PREFIX}-agent-card-collapse`
                : `${PROFILE_TEST_PREFIX}-agent-card-expand`
            }
          >
            <CollapsibleCardTitle
              title={appstr("Agent profile card")}
              isOpen={cardStatus.agentCardOpen}
              textSize="large"
              iconSize="medium"
            />
          </Flex>
          <SectionCard
            title={appstr("Agent profile desc")}
            titleContent={
              <Button
                modifier="secondary"
                onClick={() => setUpModalElement(ProfileTypes.Agent)}
                dataTestId={`${PROFILE_TEST_PREFIX}-agent-preview-button`}
              >
                {appstr("Preview")}
              </Button>
            }
            customSectionCardClass={cx(
              cardStatus.agentCardOpen ? "" : "oc-profile-configuration-card-collapsed"
            )}
            titleContainerProps={commonCardTitleContainerProps}
          >
            <AgentProfileConfig
              config={chartConfig}
              fields={agentFields}
              handleUpdate={handleUpdate}
            />
          </SectionCard>
        </Flex>
      )}
      <ProfilePreviewModal
        modalElement={modalElement}
        onClose={() => setModalElement(null)}
        isPositionChart={isPositionChart}
        chartConfig={chartConfig}
        fields={{
          [ProfileTypes.Employee]: employeeFields,
          [ProfileTypes.Position]: positionFields,
          [ProfileTypes.Agent]: agentFields
        }}
      />
    </SectionCardGroup>
  );
};
